import { type SetStateAction, useCallback, useState } from "react";
import type { OpenDocument } from "./documents/documentInfo";
import type { SourceFile } from "./messageSources";
import { useOnChatSwitch } from "./useChatSwitchKey";

/** A file an answer used, open beside the chat (MHS-1040). */
export interface ShownSourceFile {
  shown: SourceFile | null;
  /** Opens a file beside the chat, closing the document; null closes it. */
  open: (file: SourceFile | null) => void;
}

/**
 * What the person types, picks and opens in one chat: it never follows them into another (MHS-1039). When
 * `chatKey` changes (`useChatSwitchKey`) it starts again from `fresh`, read from the new chat's link: the text
 * handed over to start with and the agent in `?agent=`. Reset while rendering, so the other chat never shows the
 * last one's text, even for a frame. One panel opens beside the chat at a time: a document, or a source's file.
 */
export function usePerChatState(chatKey: number, fresh: { draft: string; agent: string | null }) {
  const [draft, setDraft] = useState(fresh.draft);
  const [pickedAgent, setPickedAgent] = useState(fresh.agent);
  const [doc, setDoc] = useState<OpenDocument | null>(null);
  const [shownFile, setShownFile] = useState<SourceFile | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  useOnChatSwitch(chatKey, () => {
    setDraft(fresh.draft);
    setPickedAgent(fresh.agent);
    setDoc(null);
    setShownFile(null);
    setShareOpen(false);
  });
  const setOpenDoc = useCallback((next: SetStateAction<OpenDocument | null>) => {
    setShownFile(null);
    setDoc(next);
  }, []);
  // Stable: the chat's answers get it through context, and they re-render on every streamed token otherwise.
  const openFile = useCallback((file: SourceFile | null) => {
    setDoc(null);
    setShownFile(file);
  }, []);
  const sourceFile: ShownSourceFile = { shown: shownFile, open: openFile };
  return {
    draft,
    setDraft,
    pickedAgent,
    setPickedAgent,
    openDoc: doc,
    setOpenDoc,
    shareOpen,
    setShareOpen,
    sourceFile,
  };
}
